"use client";

import { useRef, useEffect, useState } from "react";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import { getImageURL } from "@/utils/cdn";
import { Submission, SiteSettings, Rating } from "@/lib/api/types";

// --- TYPES ---

interface SubmissionWithLocal extends Submission {
  timestamp?: number;
  isRated?: boolean;
  reviewed?: boolean;
  image?: string;
}

interface RatingShareModalProps {
  submission: SubmissionWithLocal | null;
  siteSettings: SiteSettings;
  ratings: Rating[];
  showSlug: string;
  onClose: () => void;
  onShare: (platform: string) => void;
}

// --- HELPER FUNCTIONS ---

/**
 * Extracts the first name from a full name string.
 */
const getFirstName = (fullName?: string): string => {
  if (!fullName) return "";
  return fullName.trim().split(" ")[0];
};

/**
 * Formats the response greeting with user's name and location using template placeholders.
 * Supports placeholders: [FirstName], [City], [State]
 * Example template: "Hello [FirstName] from [City], [State]:"
 * If a placeholder's value is missing, it will be replaced with an empty string.
 */
const formatResponseGreeting = (
  greeting: string,
  name?: string,
  city?: string,
  state?: string
): string => {
  const firstName = getFirstName(name);

  return greeting
    .replace(/\[FirstName\]/g, firstName || '')
    .replace(/\[City\]/g, city || '')
    .replace(/\[State\]/g, state || '')
    .trim();
};

/**
 * Gets the response text to display - either custom text or selected dropdown option.
 * Returns null if no response is set (will trigger quote fallback view).
 */
const getResponseText = (
  submission: SubmissionWithLocal,
  siteSettings: SiteSettings
): string | null => {
  // If there's custom response text, use it
  if (submission.responseText) {
    return submission.responseText;
  }

  // Otherwise, try to use the dropdown value
  if (submission.responseDropdownValue) {
    const optionKey =
      `responseOption${submission.responseDropdownValue}` as keyof SiteSettings;
    const optionValue = siteSettings[optionKey];
    if (typeof optionValue === "string") {
      return optionValue;
    }
  }

  // If no response is set, return null to trigger fallback view
  return null;
};

/**
 * Gets the user's original take text for the quote fallback view.
 */
const getOriginalTakeText = (submission: SubmissionWithLocal): string => {
  if (submission.cleaned_text) {
    return submission.cleaned_text;
  }

  if (submission.original_text) {
    return submission.original_text;
  }

  return "Thank you for your submission!";
};

/**
 * Gets the badge image URL from the ratings array based on the submission's rating.
 */
const getBadgeImage = (
  submission: SubmissionWithLocal,
  ratings: Rating[]
): string | null => {
  // Determine the rating ID to look up
  const ratingId = submission.specialRating || submission.rating?.toString();

  if (!ratingId) return null;

  // Find the matching rating and return its image
  const rating = ratings.find((r) => r.id === ratingId);
  return rating?.image || null;
};

// --- COMPONENT ---

export default function RatingShareModal({
  submission,
  siteSettings,
  ratings,
  showSlug,
  onClose,
  onShare,
}: RatingShareModalProps) {
  const closeModalButtonRef = useRef<HTMLButtonElement | null>(null);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Effect to focus close button when modal opens
  useEffect(() => {
    if (submission) {
      setTimeout(() => closeModalButtonRef.current?.focus(), 100);
    }
  }, [submission]);

  if (!submission) return null;

  const responseText = getResponseText(submission, siteSettings);
  const originalTakeText = getOriginalTakeText(submission);
  const badgeImage = getBadgeImage(submission, ratings);
  const responseGreeting = formatResponseGreeting(
    siteSettings.responseGreeting || "Thank you for your submission!",
    submission.name,
    submission.city,
    submission.state
  );

  // Determine if we're showing the quote fallback view (no response text)
  const showQuoteView = !responseText;

  // Dynamic background image with fallback
  const backgroundImage = siteSettings.responseBackgroundImage
    ? `url('${getImageURL(siteSettings.responseBackgroundImage)}')`
    : "url('/images/ReviewBG.jpg')";

  const handleDownloadImage = async () => {
    if (!modalContentRef.current) return;
    
    setIsDownloading(true);
    onShare("Download image");

    try {
      // Temporarily hide the close button
      const closeButton = closeModalButtonRef.current;
      const originalCloseDisplay = closeButton?.style.display || '';
      if (closeButton) {
        closeButton.style.display = 'none';
      }

      // Temporarily show the mobile QR code
      const mobileQRElements = modalContentRef.current.querySelectorAll('.md\\:hidden');
      const mobileQR = Array.from(mobileQRElements).find(el => 
        el.querySelector('svg[viewBox="0 0 256 256"]')
      ) as HTMLElement | undefined;
      const originalQRDisplay = mobileQR?.style.display || '';
      if (mobileQR) {
        mobileQR.style.display = 'block';
      }

      const canvas = await html2canvas(modalContentRef.current, {
        backgroundColor: null,
        scale: 2, // Higher quality
        useCORS: true, // Handle cross-origin images
        logging: false,
      });

      // Restore the close button and mobile QR code
      if (closeButton) {
        closeButton.style.display = originalCloseDisplay;
      }
      if (mobileQR) {
        mobileQR.style.display = originalQRDisplay;
      }

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.download = `rating-${submission?.name?.replace(/\s+/g, "-") || "card"}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        }
        setIsDownloading(false);
      }, "image/png");
    } catch (error) {
      console.error("Failed to download image:", error);
      alert("Failed to download image. Please try again.");
      setIsDownloading(false);
      
      // Make sure to restore the close button on error
      const closeButton = closeModalButtonRef.current;
      if (closeButton) {
        closeButton.style.display = '';
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex flex-col items-center justify-center bg-black/80">
      <div
        ref={modalContentRef}
        className="relative w-[90%] max-w-[950px] rounded-t-2xl bg-cover bg-center px-8 pb-4 pt-8 text-black shadow-[0_5px_15px_rgba(0,0,0,0.5)] md:max-h-[90vh] md:overflow-y-auto md:px-8 md:pb-4 md:pt-8"
        style={{ backgroundImage }}
      >
        {/* --- Close button --- */}
        <button
          ref={closeModalButtonRef}
          className="absolute right-[15px] top-[15px] z-10 h-[30px] w-[30px] cursor-pointer border-none bg-transparent p-0"
          onClick={onClose}
          aria-label="Close rating card"
        >
          <img
            src={getImageURL("/images/close_white.png")}
            alt="Close"
            className="h-full w-full object-contain"
          />
        </button>

        <div className="flex flex-col gap-0 md:flex-row md:gap-8">
          {/* --- LEFT COLUMN --- */}
          <div className="flex flex-[0_0_25%] flex-col items-center text-center text-white">
            {/* Show Logo */}
            {siteSettings.showLogo && (
              <>
                <img
                  src={getImageURL(siteSettings.showLogo)}
                  alt="Show Logo"
                  className="hidden w-full max-w-[180px] md:block"
                />
                <img
                  src={getImageURL(siteSettings.showLogo)}
                  alt="Show Logo"
                  className="block w-full max-w-[180px] md:hidden"
                />
              </>
            )}

            {/* Fallback to original logos if showLogo not set */}
            {!siteSettings.showLogo && (
              <>
                <img
                  src={getImageURL("/images/Card_SAS_Lockup.png")}
                  alt="Stephen A. Smith"
                  className="hidden w-full max-w-[180px] md:block"
                />
                <img
                  src={getImageURL("/images/Card_SAS_Lockup_Mobile.png")}
                  alt="Stephen A. Smith"
                  className="block w-full max-w-[180px] md:hidden"
                />
              </>
            )}

            {/* QR Code Label */}
            {siteSettings.qrLabelText && (
              <p className="my-0 mb-[3px] mt-2 hidden text-[0.9rem] font-normal md:block">
                {siteSettings.qrLabelText}
              </p>
            )}

            {/* QR Code - Generated dynamically */}
            <div className="hidden w-full max-w-[150px] bg-white p-2 md:block">
              <QRCode
                value={
                  siteSettings.qrCodeURL ||
                  `${
                    typeof window !== "undefined" ? `${window.location.origin}` : ""
                  }/${showSlug}`
                }
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                viewBox={`0 0 256 256`}
              />
            </div>
          </div>

          {/* --- RIGHT COLUMN --- */}
          <div className="relative flex flex-1 flex-col">
            {/* Headline and Subheadline */}
            <div className="mb-4 text-left text-white">
              <p className="m-0 leading-[1.2] md:text-xl">
                {siteSettings.headlineResponse || "Official Review From"}
              </p>
              <p className="m-0 text-[1.8rem] font-extrabold leading-[1.2] md:text-[2.2rem]">
                {siteSettings.subheadlineResponse || "STEPHEN A. SMITH"}
              </p>
            </div>

            <div className="relative w-[98%]">
              {/* Yellow background layer */}
              <div 
                className="absolute left-0 top-0 h-full w-[98%] translate-x-[10px] translate-y-[15px] rotate-[-2deg] md:translate-x-[18px]"
                style={{ backgroundColor: siteSettings.shareModalAccentColor || '#ffcf00' }}
              ></div>

              {/* White content box */}
              <div className="relative z-[2] h-full bg-white p-6 md:p-8">
                {showQuoteView ? (
                  /* Quote View - Show original take when no response is set */
                  <>
                    {/* Opening Quote Mark */}
                    <div className="-mb-4">
                      <span className="text-[4rem] font-bold leading-none text-[#7b2e8d] md:text-[5rem]">
                        &ldquo;
                      </span>
                    </div>

                    {/* User's Take as Quote */}
                    <blockquote className="m-0 mb-4 -mt-2 text-left text-xl font-normal leading-snug text-black md:mb-5 md:text-2xl md:leading-relaxed">
                      {originalTakeText}
                    </blockquote>

                    {/* User's Name and Location - Below the quote */}
                    <div className="-mt-2 mb-4 md:mb-5">
                      <p className="mb-0 text-lg font-bold text-gray-500 md:text-xl">
                        {submission.name}
                      </p>
                      {(submission.city || submission.state) && (
                        <p className="mt-1 text-base font-normal text-gray-500 md:text-lg">
                          {submission.city}
                          {submission.city && submission.state && ", "}
                          {submission.state}
                        </p>
                      )}
                    </div>

                    {/* Final Review Label */}
                    <p className="mb-0 break-words pr-4 text-2xl text-md font-bold uppercase tracking-wide leading-tight md:pr-8">
                      Final Review:
                    </p>
                    <p className="mt-0 max-w-[65%] break-words pr-2 text-3xl font-extrabold leading-tight text-[#e4002b] md:max-w-none md:pr-8 md:text-4xl">
                      {ratings.find(
                        (r) =>
                          r.id ===
                          (submission.specialRating ||
                            submission.rating?.toString())
                      )?.name?.toUpperCase() || "NOW THAT IS SERIOUS"}
                    </p>
                  </>
                ) : (
                  /* Response View - Show producer's response */
                  <>
                    {/* Response Greeting */}
                    <p className="mb-4 text-lg font-bold text-black md:text-xl">
                      {responseGreeting}
                    </p>

                    {/* Response Text */}
                    <p className="mb-4 text-base leading-relaxed text-black md:text-lg">
                      {responseText}
                    </p>

                    {/* Optional Response Link */}
                    {submission.responseLink && (
                      <a
                        href={submission.responseLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-4 inline-block text-base font-semibold text-blue-600 underline hover:text-blue-800 md:text-lg"
                      >
                        {submission.responseLink}
                      </a>
                    )}

                    {/* Optional Closing Signature Image */}
                    {siteSettings.closingSignatureImage && (
                      <div className="mt-4">
                        <img
                          src={getImageURL(siteSettings.closingSignatureImage)}
                          alt="Signature"
                          className="max-h-[60px] object-contain"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Rating Badge - Fixed in lower right corner - Always shown from ratings array */}
              {badgeImage && (
                <img
                  src={getImageURL(badgeImage)}
                  className="absolute -bottom-[30px] right-0 z-[3] w-[120px] -rotate-[10deg] md:-right-0 md:w-[160px]"
                  alt="Rating Badge"
                />
              )}
            </div>

            {/* --- DESKTOP-ONLY TUNE-IN FOOTER --- */}
            {(siteSettings.footerTuneInCopyA ||
              siteSettings.footerTuneInCopyB) && (
              <div className="mt-auto hidden flex-row items-center gap-3 pb-8 pt-10 md:flex">
                <div className="flex flex-1 items-center justify-center gap-3">
                  {siteSettings.footerTuneInCopyA && (
                    <div className="shrink-0 text-right text-xs leading-tight text-white">
                      {siteSettings.footerTuneInCopyA}
                    </div>
                  )}
                  {siteSettings.footerChannelLogo1 && (
                    <div className="flex items-center">
                      <img
                        alt="Channel Logo 1"
                        src={getImageURL(siteSettings.footerChannelLogo1)}
                        className="block max-h-[40px]"
                      />
                    </div>
                  )}
                  {siteSettings.footerTuneInCopyB && (
                    <div className="text-xs leading-tight text-white">
                      {siteSettings.footerTuneInCopyB}
                    </div>
                  )}
                  {siteSettings.footerChannelLogo2 && (
                    <div className="flex items-center">
                      <img
                        alt="Channel Logo 2"
                        src={getImageURL(siteSettings.footerChannelLogo2)}
                        className="block max-h-[40px]"
                      />
                    </div>
                  )}
                </div>
                <img
                  src={getImageURL("/images/Card_SiriusXM_Logo.png")}
                  alt="SiriusXM"
                  className="block w-[100px] shrink-0"
                />
              </div>
            )}

            {/* --- MOBILE SIRIUSXM LOGO --- */}
            <img
              src={getImageURL("/images/Card_SiriusXM_Logo.png")}
              alt="SiriusXM"
              className="mx-auto mt-5 block max-h-[15px] md:hidden"
            />

            {/* --- MOBILE QR CODE (hidden by default, shown during screenshot) --- */}
            <div className="mx-auto mt-4 hidden w-full max-w-[120px] bg-white p-2 md:hidden">
              <QRCode
                value={
                  siteSettings.qrCodeURL ||
                  `${
                    typeof window !== "undefined" ? `${window.location.origin}` : ""
                  }/${showSlug}`
                }
                size={256}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                viewBox={`0 0 256 256`}
              />
            </div>
          </div>
        </div>
      </div>

      {/* --- DOWNLOAD BAR --- */}
      <div className="w-[90%] max-w-[950px] rounded-b-2xl bg-white p-4">
        <div className="mx-auto flex w-fit items-center gap-4">
          <button
            onClick={handleDownloadImage}
            disabled={isDownloading}
            className="flex items-center gap-2 cursor-pointer border-none bg-transparent p-0 text-black disabled:opacity-50 transition-opacity hover:opacity-80"
            title="Download as image"
          >
            {isDownloading ? (
              <span className="text-sm font-semibold">Downloading...</span>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-6 w-6"
                >
                  <path d="M11 5a1 1 0 112 0v7.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L11 12.586V5z" />
                  <path d="M4 14a1 1 0 011 1v3a1 1 0 001 1h12a1 1 0 001-1v-3a1 1 0 112 0v3a3 3 0 01-3 3H6a3 3 0 01-3-3v-3a1 1 0 011-1z" />
                </svg>
                <span className="text-sm font-semibold">Download this image</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
